import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Quote, Heart, Plus, Loader2, Trash2 } from "lucide-react";

export default function Testimonials() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", relationship: "", message: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials");
      setItems(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.first_name.trim() || !form.message.trim())
      return toast.error("Le prénom et le message sont nécessaires.");
    setSaving(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Merci pour votre témoignage 💚");
      setOpen(false);
      setForm({ first_name: "", relationship: "", message: "" });
      load();
    } catch {
      toast.error("Envoi impossible.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setItems((its) => its.filter((x) => x.id !== id));
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F0E8] font-care">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm font-semibold">Témoignages</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tableau-de-bord")} data-testid="testimonials-back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Tableau de bord
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {/* Warm intro */}
        <section className="text-center max-w-2xl mx-auto mb-12" data-testid="testimonials-intro">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-100 text-rose-500 mb-4">
            <Heart className="w-7 h-7" />
          </div>
          <h1 className="font-serif-display text-4xl font-normal text-gray-800 mb-4">Vos histoires nous rassemblent</h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Chaque expérience partagée ici aide d'autres familles à se sentir moins seules et à mieux
            comprendre le quotidien de l'accompagnement. Vos mots, vos petites victoires et vos moments
            de tendresse deviennent une lumière pour celles et ceux qui débutent ce chemin. Merci de
            faire vivre cette communauté bienveillante.
          </p>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 px-6" data-testid="share-testimonial-button">
                <Plus className="w-4 h-4 mr-1" /> Partager mon expérience
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="testimonial-dialog">
              <DialogHeader>
                <DialogTitle>Partager mon expérience</DialogTitle>
                <DialogDescription>Quelques mots suffisent — ils comptent beaucoup.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Prénom</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    placeholder="Votre prénom" className="mt-1" data-testid="testimonial-firstname-input" />
                </div>
                <div>
                  <Label>Votre lien avec la personne</Label>
                  <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                    placeholder="Ex : Fille d'un résident, aidant, infirmier…" className="mt-1" data-testid="testimonial-relationship-input" />
                </div>
                <div>
                  <Label>Votre message</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Partagez un souvenir, un conseil, une émotion…" className="mt-1 min-h-[120px]" data-testid="testimonial-message-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="submit-testimonial-button">
                  {saving ? "Envoi..." : "Envoyer mon témoignage"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </section>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-400">Soyez le premier à partager votre expérience.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]" data-testid="testimonials-list">
            {items.map((t) => (
              <Card key={t.id} className="border-gray-200 mb-6 break-inside-avoid" data-testid={`testimonial-${t.id}`}>
                <CardContent className="p-6 relative">
                  <Quote className="w-8 h-8 text-emerald-200 mb-3" />
                  <p className="text-gray-700 leading-relaxed mb-5 whitespace-pre-wrap">{t.message}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center">
                      {t.first_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{t.first_name}</p>
                      {t.relationship && <p className="text-sm text-gray-500">{t.relationship}</p>}
                    </div>
                  </div>
                  {user?.role === "admin" && (
                    <button onClick={() => remove(t.id)} data-testid={`delete-testimonial-${t.id}`}
                      className="absolute top-4 right-4 text-gray-300 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
